import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { message, Modal } from 'antd';
import { ReactComponent as UpArrow } from '../../icons/sort_asc.svg';
import { ReactComponent as DownArrow } from '../../icons/sort_desc.svg';
import { ReactComponent as DeleteIcon } from '../../icons/trash.svg';
import { ReactComponent as MessageIcon } from '../../icons/messages.svg';
import NameImage from '../client/NameImage';
import { getRandomColor } from '../../constants/palette';
import useHirePortTheme from '../../hooks/useHirePortTheme';
import { JOBS, RECRUITER } from '../../constants/routes';
import TooltipPrompt from '../../UI/tootlip';
import ListAction from '../jobs/jobList/ListAction';
import { removeJobAssignment } from '../../api/recruiter';
import { JobAssignment, PaginationInfo } from '../../hooks/useJobAssignments';
import SmartPagination from './SmartPagination';

// Match the exact styling from RecruiterManagementTable.tsx
const JobAssignmentsTableDiv = styled.div`
  padding: 0 2rem;
  background: #ffffff;
  border-radius: ${({ theme }) => theme.radius};
  @media (max-width: 576px) {
    padding: 0 1rem;
  }
`;

// Match the exact table header styling from RecruiterSelectTableHeader.tsx
const TableHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  border-bottom: 1px solid #DAE5EC;
  padding: 12px 0;
  color: #627D95;
  font-weight: 600;
  font-size: 0.75rem;
  line-height: 1.33;
  letter-spacing: 0.8px;
`;

const TableHeaderCell = styled.div<{ flex?: string; textAlign?: string }>`
  flex: ${props => props.flex || '1'};
  text-align: ${props => props.textAlign || 'left'};
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.33;
  letter-spacing: 0.8px;
  color: #627D95;
`;

const HoverDiv = styled.div`
  &:hover {
    cursor: pointer;
  }
`;

const StyledUpArrow = styled(UpArrow)`
  & path {
    stroke: ${(props) => props.color};
  }
`;

const StyledDownArrow = styled(DownArrow)`
  & path {
    stroke: ${(props) => props.color};
  }
`;

const TableRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  padding: 1rem 0;
  border-bottom: 1px solid #dae5ec;
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.div<{ flex?: string; clickable?: boolean }>`
  flex: ${props => props.flex || '1'};
  padding: 0 0.5rem;
  font-size: 0.875rem;
  line-height: 1.43;
  color: #061c2e;
  
  ${props => props.clickable && `
    cursor: pointer;
    &:hover {
      color: ${props.theme?.primaryColor || '#34ce72'};
    }
  `}
`;

const JobTitle = styled.span`
  font-weight: 600;
  cursor: pointer;
  color: #061c2e;
  
  &:hover {
    color: ${({ theme }) => theme.primaryColor};
  }
`;

const CompanyName = styled.span`
  font-size: 0.75rem;
  color: #627d95;
  display: block;
  margin-top: 0.25rem;
`;

const StatusBadge = styled.span<{ status: 'active' | 'paused' | 'inactive' }>`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => {
        switch (props.status) {
            case 'active':
                return '#eaf7ef';
            case 'paused':
                return '#fff7e6';
            case 'inactive':
                return '#f2f6fa';
            default:
                return '#f2f6fa';
        }
    }};
  color: ${props => {
        switch (props.status) {
            case 'active':
                return '#29ae5e';
            case 'paused':
                return '#d46b08';
            case 'inactive':
                return '#627d95';
            default:
                return '#627d95';
        }
    }};
`;

const EnrichmentStatus = styled.span<{ canEnrich: boolean }>`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => props.canEnrich ? '#29ae5e' : '#627d95'};
  display: flex;
  align-items: center;
  
  &::before {
    content: ${props => props.canEnrich ? '"✓"' : '"✗"'};
    margin-right: 0.25rem;
    font-weight: bold;
  }
`;

const DateText = styled.span`
  font-size: 0.75rem;
  color: #627d95;
`;

const TargetText = styled.span`
  font-size: 0.75rem;
  color: #061c2e;
  font-weight: 500;
`;

const TargetType = styled.span`
  font-size: 0.625rem;
  color: #627d95;
  display: block;
  text-transform: capitalize;
`;



const ActionCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Fix MessageIcon to inherit color properly (it has hardcoded stroke color)
const StyledMessageIcon = styled(MessageIcon)`
  & path {
    stroke: currentColor !important;
  }
`;

// Custom action button for the assignments table
const AssignmentActionButton = styled.button`
  width: 100%;
  padding: 0.25rem 0.5rem;
  border: 1px solid ${({ theme }) => theme.primaryColor};
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1.33;
  text-align: left;
  color: ${({ theme }) => theme.primaryColor};
  display: flex;
  align-items: center;
  background: #ffffff;
  margin: 0.25rem 0;
  cursor: pointer;
  
  &:hover {
    border-color: ${({ theme }) => theme.hoverColor};
    color: ${({ theme }) => theme.hoverColor};
  }
  
  & svg {
    font-size: 1rem;
    margin-right: 0.5rem;
    width: 1rem;
    height: 1rem;
  }
  
  &.danger {
    color: #eb5757;
    border-color: #eb5757;
    
    &:hover {
      border-color: #d63031;
      color: #d63031;
    }
  }
`;

interface JobAssignmentsTableProps {
    assignments: JobAssignment[];
    pagination: PaginationInfo | null;
    onSort: (sortField: string) => void;
    onPageChange: (page: number) => void;
    currentSort: string;
    onAssignmentRemoved?: () => void; // Callback to refresh data
    isAggregated?: boolean; // Whether data is aggregated by company
}

const TableHeaderName = ({
    name,
    sortField,
    currentSort,
    onSort,
}: {
    name: string;
    sortField: string;
    currentSort: string;
    onSort: (sortField: string) => void;
}) => {
    const theme = useHirePortTheme();
    const isCurrentSort = currentSort === sortField || currentSort === `-${sortField}`;
    const isDesc = currentSort === `-${sortField}`;

    let upArrowColor = '#627d95';
    let downArrowColor = '#627d95';

    if (isCurrentSort) {
        if (isDesc) {
            downArrowColor = theme.primaryColor;
            upArrowColor = '#627d95';
        } else {
            upArrowColor = theme.primaryColor;
            downArrowColor = '#627d95';
        }
    }

    return (
        <HoverDiv
            style={{ display: 'flex', alignItems: 'center' }}
            onClick={() => onSort(sortField)}
        >
            <span
                style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    marginRight: '0.5rem'
                }}
            >
                <StyledDownArrow
                    color={downArrowColor}
                    style={{ marginBottom: '-9px' }}
                />
                <StyledUpArrow color={upArrowColor} />
            </span>
            {name}
        </HoverDiv>
    );
};

const JobAssignmentsTable: React.FC<JobAssignmentsTableProps> = ({
    assignments,
    pagination,
    onSort,
    onPageChange,
    currentSort,
    onAssignmentRemoved,
    isAggregated
}) => {
    const [t] = useTranslation();
    const history = useHistory();
    const { confirm } = Modal;

    const handleJobClick = (jobId: string) => {
        history.push(`${JOBS}/${jobId}`);
    };

    const handleRecruiterClick = (recruiterId: string) => {
        history.push(`${RECRUITER}/${recruiterId}`);
    };

    const handleSendMessage = () => {
        message.info(t('MESSAGE_FEATURE_COMING_SOON', 'Messaging feature coming soon'));
    };

    const handleRemoveAssignment = (assignment: JobAssignment) => {
        confirm({
            title: t('REMOVE_ASSIGNMENT_CONFIRM_TITLE', 'Remove Assignment'),
            content: t('REMOVE_ASSIGNMENT_CONFIRM_MESSAGE',
                `Are you sure you want to remove ${assignment.recruiter_name} from "${assignment.job_title}"?`),
            okText: t('REMOVE', 'Remove'),
            okType: 'danger',
            cancelText: t('CANCEL', 'Cancel'),
            onOk: async () => {
                try {
                    await removeJobAssignment(assignment.assignment_id);
                    message.success(t('ASSIGNMENT_REMOVED', 'Assignment removed successfully'));
                    if (onAssignmentRemoved) {
                        onAssignmentRemoved();
                    }
                } catch (error: any) {
                    if (error.response?.data?.error === 'candidates_in_process') {
                        message.error(t('CANNOT_REMOVE_ASSIGNMENT_CANDIDATES_IN_PROCESS',
                            'Unable to remove assignment. Candidates are in process.'));
                    } else {
                        message.error(t('ERROR_REMOVING_ASSIGNMENT', 'Something went wrong, please try again.'));
                    }
                }
            },
        });
    };

    const formatTarget = (targetType: string, targetValue: number) => {
        if (!targetType || targetValue === null || targetValue === undefined) {
            return { text: t('NO_TARGET', 'No target'), type: '' };
        }

        let formattedValue = '';
        if (targetType === 'percentage') {
            formattedValue = `${targetValue}%`;
        } else if (targetType === 'fixed') {
            formattedValue = `€${targetValue.toLocaleString()}`;
        } else {
            formattedValue = targetValue.toString();
        }

        return {
            text: formattedValue,
            type: targetType
        };
    };

    const getAssignmentStatus = (assignment: JobAssignment): 'active' | 'paused' | 'inactive' => {
        if (!assignment.is_active) {
            return 'inactive';
        }

        // Assignment is active, check if job is published
        const isJobOnline = assignment.job_status === 'published' || assignment.job_status === 'under review';
        return isJobOnline ? 'active' : 'paused';
    };

    const getStatusText = (status: 'active' | 'paused' | 'inactive') => {
        switch (status) {
            case 'active':
                return t('ACTIVE', 'Active');
            case 'paused':
                return t('PAUSED', 'Paused');
            case 'inactive':
                return t('INACTIVE', 'Inactive');
            default:
                return t('INACTIVE', 'Inactive');
        }
    };

    return (
        <JobAssignmentsTableDiv>
            {/* Table Header - Using proper styled components */}
            <TableHeader>
                <TableHeaderCell flex="26%">
                    <TableHeaderName
                        name={isAggregated ? t('AGENCY_COMPANY', 'Agency Company') : t('AGENCY_USER', 'Agency User')}
                        sortField="recruiter_name"
                        currentSort={currentSort}
                        onSort={onSort}
                    />
                </TableHeaderCell>
                <TableHeaderCell flex="30%">
                    <TableHeaderName
                        name={t('JOB', 'Job')}
                        sortField="job_title"
                        currentSort={currentSort}
                        onSort={onSort}
                    />
                </TableHeaderCell>
                <TableHeaderCell flex="14%">
                    {t('STATUS', 'Status')}
                </TableHeaderCell>
                <TableHeaderCell flex="14%">
                    {t('TARGET', 'Target')}
                </TableHeaderCell>
                <TableHeaderCell flex="12%">
                    {t('ENRICH', 'Enrich')}
                </TableHeaderCell>
                <TableHeaderCell flex="10%">
                    <TableHeaderName
                        name={t('ASSIGNMENT_DATE', 'Assigned')}
                        sortField="assignment_date"
                        currentSort={currentSort}
                        onSort={onSort}
                    />
                </TableHeaderCell>
                <TableHeaderCell flex="4%">
                    {t('ACTIONS', 'Actions')}
                </TableHeaderCell>
            </TableHeader>

            {/* Table Rows */}
            {assignments.map((assignment) => {
                const target = formatTarget(assignment.current_target_type, assignment.current_target_value);
                const isAggregatedRow = assignment.assignment_id.startsWith('aggregated-');

                return (
                    <TableRow key={assignment.assignment_id}>
                        <TableCell flex="26%">
                            {isAggregatedRow ? (
                                <div>
                                    <JobTitle style={{ cursor: 'default' }}>
                                        {assignment.recruiter_company_name}
                                    </JobTitle>
                                    <CompanyName>{assignment.recruiter_name}</CompanyName>
                                </div>
                            ) : (
                                <TooltipPrompt title={t('CLICK_TO_VIEW_RECRUITER_PROFILE', 'Click to view agency user profile')}>
                                    <div onClick={() => handleRecruiterClick(assignment.recruiter_id)} style={{ cursor: 'pointer' }}>
                                        <NameImage
                                            title={assignment.recruiter_name}
                                            description={assignment.recruiter_company_name}
                                            image={assignment.recruiter_company_image}
                                            color={getRandomColor()}
                                        />
                                    </div>
                                </TooltipPrompt>
                            )}
                        </TableCell>
                        <TableCell flex="30%">
                            <TooltipPrompt title={t('CLICK_TO_VIEW_JOB_DETAILS', 'Click to view job details')}>
                                <div>
                                    <JobTitle onClick={() => handleJobClick(assignment.job_id)}>
                                        {assignment.job_title}
                                    </JobTitle>
                                    <CompanyName>{assignment.job_company_name}</CompanyName>
                                </div>
                            </TooltipPrompt>
                        </TableCell>
                        <TableCell flex="14%">
                            {(() => {
                                const status = getAssignmentStatus(assignment);
                                const statusBadge = (
                                    <StatusBadge status={status}>
                                        {getStatusText(status)}
                                    </StatusBadge>
                                );

                                // Add tooltip for paused status
                                if (status === 'paused') {
                                    return (
                                        <TooltipPrompt title={t('PAUSED_ASSIGNMENT_TOOLTIP', 'Job is offline - assignment will resume when job is published')}>
                                            {statusBadge}
                                        </TooltipPrompt>
                                    );
                                }

                                return statusBadge;
                            })()}
                        </TableCell>
                        <TableCell flex="14%">
                            <TargetText>{target.text}</TargetText>
                            {target.type && (
                                <TargetType>{target.type}</TargetType>
                            )}
                        </TableCell>
                        <TableCell flex="12%">
                            <EnrichmentStatus canEnrich={assignment.can_answer_extension_fields}>
                                {assignment.can_answer_extension_fields
                                    ? t('CAN_ENRICH', 'Can enrich')
                                    : t('CANNOT_ENRICH', 'Cannot enrich')
                                }
                            </EnrichmentStatus>
                        </TableCell>
                        <TableCell flex="10%">
                            <DateText>
                                {new Date(assignment.assignment_date).toLocaleDateString()}
                            </DateText>
                        </TableCell>
                        <TableCell flex="4%">
                            <ActionCell>
                                {!isAggregatedRow && (
                                    <ListAction
                                        status={getAssignmentStatus(assignment) === 'active' ? 'active' : 'inactive'}
                                        id={assignment.assignment_id}
                                        content={
                                            <>
                                                <AssignmentActionButton onClick={handleSendMessage}>
                                                    <StyledMessageIcon />
                                                    {t('SEND_MESSAGE', 'Send Message')}
                                                </AssignmentActionButton>
                                                {getAssignmentStatus(assignment) === 'active' && (
                                                    <AssignmentActionButton onClick={() => handleRemoveAssignment(assignment)} className="danger">
                                                        <DeleteIcon />
                                                        {t('REMOVE_ASSIGNMENT', 'Remove Assignment')}
                                                    </AssignmentActionButton>
                                                )}
                                            </>
                                        }
                                    />
                                )}
                                {isAggregatedRow && (
                                    <span style={{ fontSize: '0.75rem', color: '#627d95' }}>
                                        {t('COMPANY_VIEW', 'Company view')}
                                    </span>
                                )}
                            </ActionCell>
                        </TableCell>
                    </TableRow>
                );
            })}

            {/* Pagination */}
            {pagination && (
                <SmartPagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.total_pages}
                    onPageChange={onPageChange}
                    showInfo={true}
                    totalItems={pagination.count}
                    itemsPerPage={pagination.page_size}
                />
            )}
        </JobAssignmentsTableDiv>
    );
};

export default JobAssignmentsTable; 